import { useEffect, useMemo, useState, ReactNode } from "react";
import { BriefcaseIcon, UserGroupIcon } from "@heroicons/react/solid";
import {
  BriefcaseIcon as BriefcaseIconOutline,
  UserGroupIcon as UserGroupIconOutline,
  CalendarIcon,
  ShieldCheckIcon,
  CreditCardIcon,
  UsersIcon,
  CheckIcon,
  XIcon,
  UserIcon,
  UserCircleIcon,
  KeyIcon,
} from "@heroicons/react/outline";
import { useSearchParams } from "react-router-dom";
import moment from "moment";

import Workspaces from "../Workspaces";
import OrganizationMembers from "../OrganizationMembers";
import { RolesList } from "../RolesList";

import { Organization } from "@/types/organizations";
import EnterpriseIdentityPage from "@/routes/settings/EnterpriseIdentity/page";
import { useWorkspaces } from "@/queries";
import { Badge, BadgeSize, BadgeVariant } from "@/components/ui/Badge";
import { capitalize } from "@/utils/strings";
import TabNavigation from "@/components/ui/TabNavigation";
import { useOrganizationSubscription } from "@/queries/organizations";
import { PL_ADMIN_PLAN_NAME } from "@/providers/OrganizationSubscriptionProvider";

interface OrganizationDetailsProps {
  organization: Organization;
  userRole: string;
}

interface InfoCardConfig {
  icon: ReactNode;
  label: string;
  content: ReactNode;
}

export const OrganizationDetails = ({ organization, userRole }: OrganizationDetailsProps) => {
  const showEnterpriseIdentityTab =
    userRole === "owner" && organization.is_enterprise_identity_enabled;
  const { data: workspacesData } = useWorkspaces();
  const { data: organizationSubscriptionData } = useOrganizationSubscription(organization.id);
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(0);

  const organizationWorkspaces = useMemo(
    () => workspacesData?.workspaces.filter(w => w.organization_id === organization.id) || [],
    [workspacesData, organization.id],
  );

  const infoCardsConfig = useMemo<InfoCardConfig[]>(() => {
    const planType = organizationSubscriptionData?.subscription?.plan_type;
    const subscriptionLabel = planType === "pl_admin" ? PL_ADMIN_PLAN_NAME : planType || "-";

    const rbacEnabled = organization.is_rbac_enabled;

    return [
      {
        icon: <CalendarIcon className="h-3.5 w-3.5 text-gray-500" />,
        label: "Created",
        content: moment(organization?.created_at || "").format("MM/DD/YYYY"),
      },
      {
        icon: <UserIcon className="h-3.5 w-3.5 text-gray-500" />,
        label: "Your Role",
        content: (
          <Badge
            size={BadgeSize.SM}
            variant={userRole === "owner" ? BadgeVariant.BLUE : BadgeVariant.GRAY}
          >
            {capitalize(userRole)}
          </Badge>
        ),
      },
      {
        icon: <CreditCardIcon className="h-3.5 w-3.5 text-gray-500" />,
        label: "Subscription",
        content: (
          <Badge
            size={BadgeSize.SM}
            variant={planType === "free" ? BadgeVariant.GRAY : BadgeVariant.BLUE}
          >
            {capitalize(subscriptionLabel)}
          </Badge>
        ),
      },
      {
        icon: <ShieldCheckIcon className="h-3.5 w-3.5 text-gray-500" />,
        label: "RBAC",
        content: rbacEnabled ? (
          <CheckIcon className="h-4 w-4 text-green-500" />
        ) : (
          <XIcon className="h-4 w-4 text-red-500" />
        ),
      },
      {
        icon: <UsersIcon className="h-3.5 w-3.5 text-gray-500" />,
        label: "Total Members",
        content: organization?.members_count || 0,
      },
      {
        icon: <BriefcaseIconOutline className="h-3.5 w-3.5 text-gray-500" />,
        label: "Total Workspaces",
        content: organizationWorkspaces.length,
      },
    ];
  }, [organization, userRole, organizationSubscriptionData, organizationWorkspaces.length]);

  const tabs = useMemo(() => {
    const baseTabs = [
      {
        id: 0,
        name: "Members",
        outlineIcon: <UserGroupIconOutline className="h-4 w-4" />,
        solidIcon: <UserGroupIcon className="h-4 w-4" />,
        content: (
          <div className="flex-1 overflow-hidden">
            <OrganizationMembers organizationId={organization.id} />
          </div>
        ),
      },
      {
        id: 1,
        name: "Workspaces",
        outlineIcon: <BriefcaseIconOutline className="h-4 w-4" />,
        solidIcon: <BriefcaseIcon className="h-4 w-4" />,
        content: (
          <div className="flex-1 overflow-hidden">
            <Workspaces organizationId={organization.id} />
          </div>
        ),
      },
      {
        id: 2,
        name: "Workspace Roles",
        outlineIcon: <UserCircleIcon className="h-4 w-4" />,
        solidIcon: <UserCircleIcon className="h-4 w-4" />,
        disabled: !organization.is_rbac_enabled,
        tooltipContent: !organization.is_rbac_enabled
          ? "RBAC must be enabled to view roles"
          : undefined,
        content: (
          <div className="flex-1 overflow-hidden">
            <RolesList organizationId={organization.id} />
          </div>
        ),
      },
    ];

    if (showEnterpriseIdentityTab) {
      baseTabs.push({
        id: 3,
        name: "Enterprise Identity",
        outlineIcon: <KeyIcon className="h-4 w-4" />,
        solidIcon: <KeyIcon className="h-4 w-4" />,
        content: (
          <div className="flex-1 overflow-y-auto">
            <EnterpriseIdentityPage organizationId={organization.id} />
          </div>
        ),
      });
    }

    return baseTabs;
  }, [organization.id, organization.is_rbac_enabled, showEnterpriseIdentityTab]);

  useEffect(() => {
    const tabParam = searchParams.get("tab");
    const fallbackIndex = tabs.findIndex(t => !t.disabled);
    if (fallbackIndex === -1) {
      return;
    }

    const paramIndex = tabParam ? tabs.findIndex(t => t.name.toLowerCase() === tabParam) : -1;
    const nextIndex = paramIndex !== -1 && !tabs[paramIndex]?.disabled ? paramIndex : fallbackIndex;

    setActiveTab(nextIndex);
  }, [tabs, searchParams]);

  return (
    <div className="flex h-full flex-col px-4 pb-2">
      <div className="border-gray-200 pb-1">
        <div className="mb-1.5 flex h-[32px] flex-wrap items-center gap-x-3 gap-y-1.5 text-sm">
          {infoCardsConfig.map((card, index) => (
            <>
              {" "}
              {}
              {/* eslint-disable-next-line react/no-array-index-key */}
              <div key={index} className="flex items-center gap-1.5 text-gray-600">
                {card.icon}
                <span className="text-sm text-gray-600">{card.label}: </span>
                <span className="text-sm text-gray-600">{card.content}</span>
              </div>
              {index !== infoCardsConfig.length - 1 && <div className="h-3 w-px bg-gray-300" />}
            </>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-hidden">
        <TabNavigation
          tabs={tabs}
          selectedIndex={activeTab}
          handleTabChange={index => {
            setActiveTab(index);
            setSearchParams(prev => {
              const next = new URLSearchParams(prev);
              next.set("tab", tabs[index].name.toLowerCase());
              return next;
            });
          }}
          shouldBeSticky={false}
        />
      </div>
    </div>
  );
};
